import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngxs/store';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  AccurateTimeDetails,
  Label,
  MAX_CELL_VIEW_UNASSIGNED_SHIFT_HEIGHT,
  Project,
  RoleAction,
  RolePermissionSubject,
  ShiftColorOptions,
  UnassignedShift,
} from '@wilson/interfaces';
import {
  ACTION_TOASTER_DURATION,
  NzMessageTimerLineComponent,
} from '@wilson/non-domain-specific/ng-zorro/nz-message';
import { ShiftTimelinePipesModule } from '@wilson/shift-timeline/pipes';
import { PreferredSettingsStateFacadeService } from '@wilson/shift-timeline/services';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipBaseDirective, NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Observable, Subscription } from 'rxjs';
import { UnassignedRegionCellViewShiftsDetailsRenderService } from '../../assignments-gantt-chart/unassigned-region/unassigned-region-cell-view/unassigned-region-cell-view-shifts-details-render/unassigned-region-cell-view-shifts-details-render.service';
import { DeferredUnassignedShiftCardCellViewHelperService } from '../../deferred-unassigned-shift-card-cell-view/deferred-unassigned-shift-card-cell-view-helper.service';
import { MessageWithUndoOptionComponent } from '../../message-with-undo-option/message-with-undo-option.component';
import { IsShiftSelectedPipe } from '../../pipes/is-shift-selected.pipe';
import { ShiftCardColorStylePipe } from '../../pipes/shift-card-color-style.pipe';
import { ShiftPresenceBorderColorPipe } from '../../pipes/shift-presence-border-color.pipe';
import { ShiftMultiSelectHelperService } from '../../services/shift-multi-select-helper.service';
import { ShiftCardTooltipStateService } from '../../shift-cards/tooltips/shift-card-tooltip/shift-card-tooltip-state.service';
import { ShiftValidationTooltipWrapperComponent } from '../../shift-cards/tooltips/shift-validation-tooltip-wrapper/shift-validation-tooltip-wrapper.component';
import { GroupItemsSelectionHelperService } from '../../services/group-items-selection-helper.service';

@Component({
  selector: 'wilson-cell-view-unassigned-shift-card',
  templateUrl: './cell-view-unassigned-shift-card.component.html',
  styleUrls: ['./cell-view-unassigned-shift-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzTooltipModule,
    NzSkeletonModule,
    FeatureFlagsModule,
    FontAwesomeModule,
    ShiftValidationTooltipWrapperComponent,
    IsShiftSelectedPipe,
    ShiftPresenceBorderColorPipe,
    ShiftCardColorStylePipe,
    MessageWithUndoOptionComponent,
    NzMessageTimerLineComponent,
    ShiftTimelinePipesModule,
    AuthorizationModule,
  ],
})
export class CellViewUnassignedShiftCardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly cdRef = inject(ChangeDetectorRef);
  readonly store = inject(Store);
  readonly shiftCardTooltipStateService = inject(ShiftCardTooltipStateService);
  readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  readonly unassignedRegionCellViewShiftsDetailsRenderService = inject(
    UnassignedRegionCellViewShiftsDetailsRenderService,
  );
  readonly deferredUnassignedShiftCardCellViewHelperService = inject(
    DeferredUnassignedShiftCardCellViewHelperService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );
  protected readonly groupItemsSelectionHelperService = inject(
    GroupItemsSelectionHelperService,
  );

  @ViewChild('tooltip') tooltip!: NzTooltipBaseDirective;
  @ViewChildren('shiftPreviewCard') shiftPreviewCardRef!: QueryList<
    ElementRef<HTMLElement>
  >;

  @Input() shift!: UnassignedShift;
  @Input() shiftId!: string;
  @Input() shiftCategoryId!: string;
  @Input() shiftName!: string;
  @Input({ required: true }) calculatedShiftWidth!: number | null;

  @Input() isFiltered!: boolean;
  @Input() isHighlighted!: boolean;
  @Input() isShiftDraggable = false;
  @Input() isMinimalHeight = false;
  @Input() isDragging!: boolean;
  @Input() isShiftVisible: boolean | null = false;
  @Input() isTooltipVisible: boolean | null = false;
  @Input() shiftStartDetail!: AccurateTimeDetails;
  @Input() shiftEndDetail!: AccurateTimeDetails;
  @Input() plannedShiftStartDateTime!: string;
  @Input() plannedShiftEndDateTime!: string;
  @Input() plannedStartLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() plannedEndLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() reportedStartLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input() reportedEndLocation!: {
    locationCode: string;
    name: string;
  } | null;
  @Input({ required: true }) isShiftModified!: boolean;
  @Input({ required: true }) isSelectionEnabled = false;
  @Input() project!: Project | null | undefined;
  @Input() label!: Label | null | undefined;

  @Output() previewRef = new EventEmitter<HTMLElement>();

  MAX_CELL_VIEW_UNASSIGNED_SHIFT_HEIGHT = MAX_CELL_VIEW_UNASSIGNED_SHIFT_HEIGHT;

  public readonly MESSAGE_DURATION = ACTION_TOASTER_DURATION;
  public shiftColorOption$!: Observable<ShiftColorOptions>;
  can = inject(AbilityServiceSignal).can;

  isCardVisible = true;
  shiftDuration!: number;
  private subscriptions = new Subscription();
  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;

  ngOnInit(): void {
    this.shiftColorOption$ =
      this.preferredSettingsStateFacadeService.shiftColorOptions$;
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.shiftPreviewCardRef.changes.subscribe(() => {
        if (this.shiftPreviewCardRef.get(0)) {
          this.previewRef.emit(this.shiftPreviewCardRef.get(0)?.nativeElement);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async checkShiftKeyOnClick(
    event: MouseEvent,
    undoTemplate: TemplateRef<void>,
  ): Promise<void> {
    await this.shiftMultiSelectHelperService.handleClick({
      shiftId: this.shiftId,
      event,
      leftPixelPosition: null,
      rightPixelPosition: null,
      singleShiftSelectedCallback: () => {
        // This function is intentionally left empty
      },
      templateRef: undoTemplate,
      messageDuration: this.MESSAGE_DURATION,
      isUnAssignedShift: true,
    });
  }
}
